import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import {
  Music,
  Edit,
  Trash2,
  Loader2,
  Play,
  ExternalLink,
  Upload,
  Link2,
  MoreVertical,
  X,
  Check,
  Eye,
  EyeOff,
  Calendar
} from 'lucide-react';


export default function MyMusicManager() {
  const { getAuthHeaders } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTrack, setEditingTrack] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const response = await fetch(`${API}/api/artist/tracks`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTracks(data.tracks || []);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
      toast.error('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (track) => {
    setEditingTrack({
      ...track,
      newTitle: track.title || '',
      newDescription: track.description || '',
      newVisibility: track.visibility || 'public'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTrack) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/tracks/${editingTrack.track_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: editingTrack.newTitle,
          description: editingTrack.newDescription,
          visibility: editingTrack.newVisibility
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Update the track in the list
        setTracks(prev => prev.map(t => 
          t.track_id === editingTrack.track_id ? data.track : t
        ));
        setEditingTrack(null);
        toast.success('Track updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update track');
      }
    } catch (error) {
      console.error('Error updating track:', error);
      toast.error('Failed to update track');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trackId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/tracks/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setTracks(prev => prev.filter(t => t.track_id !== trackId));
        setDeleteConfirm(null);
        toast.success('Track deleted successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete track');
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      toast.error('Failed to delete track');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-music-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">My Music</h2>
          <p className="text-sm text-muted-foreground">
            Manage your uploaded tracks ({tracks.length} total)
          </p>
        </div>
      </div>

      {/* Empty State */}
      {tracks.length === 0 && (
        <Card className="glass-card border-border p-8 text-center">
          <Music className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">No tracks yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload your first track to get started
          </p>
        </Card>
      )}

      {/* Track List */}
      <div className="space-y-3">
        {tracks.map((track) => (
          <Card 
            key={track.track_id} 
            className="glass-card border-border p-4"
            data-testid={`track-item-${track.track_id}`}
          >
            {/* Edit Mode */}
            {editingTrack?.track_id === track.track_id ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-purple-400">Editing Track</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingTrack(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs mb-1 block">Title</Label>
                    <Input
                      value={editingTrack.newTitle}
                      onChange={(e) => setEditingTrack(prev => ({
                        ...prev,
                        newTitle: e.target.value
                      }))}
                      placeholder="Track title"
                      data-testid="edit-track-title"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-1 block">Description</Label>
                    <Input
                      value={editingTrack.newDescription}
                      onChange={(e) => setEditingTrack(prev => ({
                        ...prev,
                        newDescription: e.target.value
                      }))}
                      placeholder="Track description (optional)"
                      data-testid="edit-track-description"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-1 block">Visibility</Label>
                    <select
                      value={editingTrack.newVisibility}
                      onChange={(e) => setEditingTrack(prev => ({
                        ...prev,
                        newVisibility: e.target.value
                      }))}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="edit-track-visibility"
                    >
                      <option value="public">Public - Visible in Discover</option>
                      <option value="unlisted">Unlisted - Link only</option>
                      <option value="private">Private - Only you</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingTrack(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={saving}
                    data-testid="save-track-edit"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="flex items-center gap-4">
                {/* Cover Art */}
                <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  {track.cover_art ? (
                    <img 
                      src={track.cover_art} 
                      alt={track.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Music className="w-8 h-8 text-purple-400" />
                  )}
                </div>

                {/* Track Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{track.title}</h3>
                    {track.visibility !== 'public' && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        {track.visibility === 'private' ? (
                          <><EyeOff className="w-3 h-3" /> Private</>
                        ) : (
                          <><Eye className="w-3 h-3" /> Unlisted</>
                        )}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      {track.upload_method === 'direct' ? (
                        <><Upload className="w-3 h-3" /> Direct</>
                      ) : (
                        <><Link2 className="w-3 h-3" /> {track.dsp_platform || 'DSP'}</>
                      )}
                    </span>
                    <span>•</span>
                    <span>{track.primary_genre || track.genre || 'No genre'}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Play className="w-3 h-3" />
                      {track.plays_count || 0}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(track.created_at)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {track.dsp_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(track.dsp_url, '_blank')}
                      title="Open DSP Link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(track)}
                    title="Edit Track"
                    data-testid={`edit-track-${track.track_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  {deleteConfirm === track.track_id ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(track.track_id)}
                        disabled={saving}
                        data-testid={`confirm-delete-${track.track_id}`}
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Delete'
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(track.track_id)}
                      title="Delete Track"
                      data-testid={`delete-track-${track.track_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Note */}
      {deleteConfirm && (
        <p className="text-xs text-red-400 text-center">
          Click "Delete" to permanently remove this track. This action cannot be undone.
        </p>
      )}
    </div>
  );
}
